/**
 * Staff API Client.
 *
 * Eine separate Axios-Instance gegen api.it-sce.de (admin-api Backend).
 * Wird mit dem aktuellen Keycloak-Token aus useAuth() versorgt — Auto-
 * Refresh greift weiterhin (request-Interceptor liest die Token-Funktion
 * jedes Mal).
 *
 * TQ #9696 (Staff-Migration Teil C, Bundle #9523). Portiert aus
 * customer-portal/src/services/staffApi.ts — die Staff-Pages konsumieren
 * den typed @it-sce/ui StaffApi (createStaffApi) via `useStaffApi()` Hook
 * (siehe ./useStaffApi.ts).
 */

import axios, { type AxiosInstance } from 'axios'
import { getRuntimeConfig } from '../lib/runtimeConfig'
import {
  createStaffApi,
  type StaffApi,
} from '@it-sce/ui'


/**
 * Erzeugt eine StaffApi-Instance.
 *
 * @param getToken Synchroner Getter, der den aktuellen Keycloak-Token
 *                 liefert. Wird im Request-Interceptor jedes Mal neu
 *                 evaluiert — so greift der Auto-Refresh aus
 *                 KeycloakAuthContext (der setToken() im API-State
 *                 setzt) zuverlässig.
 */
export function buildStaffApi(getToken: () => string | null): StaffApi {
  const instance: AxiosInstance = axios.create({
    baseURL: getRuntimeConfig().adminApiBase ?? '',
    headers: { 'Content-Type': 'application/json' },
  })

  instance.interceptors.request.use((config) => {
    const tok = getToken()
    if (tok) {
      config.headers.Authorization = `Bearer ${tok}`
    }
    return config
  })

  return createStaffApi(instance)
}
